import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  BarChart3, 
  HelpCircle, 
  Star,
  MessageSquare, 
  Heart, 
  Users, 
  AlertTriangle, 
  Lightbulb, 
  Target,
  FileText,
  Lock,
  ArrowDownUp,
  Grid3x3,
  Shuffle,
  Clock,
  ArrowDown,
  ArrowUp,
  CloudUpload,
  Sparkles
} from 'lucide-react';
import { NoteType, NoteMode, capturingNoteTypes, analysingNoteTypes } from '../types/notes';
import ProFeatureWrapper from './ProFeatureWrapper';
import { useSubscription } from '@/hooks/useSubscription';
import UsageIndicator from './UsageIndicator';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

interface ToolbarProps {
  onAddNote: (type: NoteType, reactFlowInstance?: any) => void;
  onAnalyze: () => void;
  onOpenGuidance: () => void;
  onOpenHighlights?: () => void;
  isAnalyzing: boolean;
  mode: NoteMode;
  onModeChange: (mode: NoteMode) => void;
  aiAnalysisEnabled?: boolean;
  liveGuidanceEnabled?: boolean;
  onToggleAiAnalysis?: (enabled: boolean) => void;
  onToggleLiveGuidance?: (enabled: boolean) => void;
  reactFlowInstance?: any;
  // Organization controls
  onGroupByType?: () => void;
  onUngroup?: () => void;
  isGrouped?: boolean;
  noteTypeCounts?: Record<NoteType, number>;
  onTimelineToggle?: () => void;
  isTimelineSorted?: boolean;
  onSortChange?: (order: 'newest' | 'oldest') => void;
  onAiOrganize?: () => void;
  isAiOrganizing?: boolean;
}

const noteTypeConfig = {
  note: { icon: FileText, label: 'General Note', color: 'bg-gray-100 hover:bg-gray-200' },
  quote: { icon: MessageSquare, label: 'Quote', color: 'bg-yellow-100 hover:bg-yellow-200' },
  emotion: { icon: Heart, label: 'Emotion', color: 'bg-pink-100 hover:bg-pink-200' },
  behaviour: { icon: Users, label: 'Behaviour', color: 'bg-blue-100 hover:bg-blue-200' },
  pain: { icon: AlertTriangle, label: 'Pain Point', color: 'bg-red-100 hover:bg-red-200' },
  insight: { icon: Lightbulb, label: 'Insight', color: 'bg-green-100 hover:bg-green-200' },
  opportunity: { icon: Target, label: 'Opportunity', color: 'bg-purple-100 hover:bg-purple-200' }
};

export const Toolbar: React.FC<ToolbarProps> = ({ 
  onAddNote, 
  onAnalyze, 
  onOpenGuidance,
  onOpenHighlights,
  isAnalyzing, 
  mode, 
  onModeChange,
  aiAnalysisEnabled = true,
  liveGuidanceEnabled = true,
  onToggleAiAnalysis,
  onToggleLiveGuidance,
  reactFlowInstance,
  // Organization props
  onGroupByType,
  onUngroup,
  isGrouped,
  noteTypeCounts = {},
  onTimelineToggle,
  isTimelineSorted,
  onSortChange,
  onAiOrganize,
  isAiOrganizing
}) => {
  const { isProUser } = useSubscription();
  const { currentUsage, limit } = useUsageTracking();
  const isLiveGuidanceAvailable = useFeatureFlag('enableLiveGuidance');

  const handleDragStart = (event: React.DragEvent<HTMLButtonElement>, noteType: NoteType) => {
    event.dataTransfer.setData('application/reactflow', 'stickyNote');
    event.dataTransfer.setData('application/json', JSON.stringify({ noteType }));
    event.dataTransfer.effectAllowed = 'move';
  };

  const currentNoteTypes = mode === 'capturing' ? capturingNoteTypes : analysingNoteTypes;
  const totalNotes = Object.values(noteTypeCounts).reduce((sum, count) => sum + count, 0);

  const noteTypeLabels: Record<NoteType, string> = {
    note: 'Notes',
    quote: 'Quotes', 
    emotion: 'Emotions',
    behaviour: 'Behaviours',
    pain: 'Pain Points',
    insight: 'Insights',
    opportunity: 'Opportunities'
  };

  const noteTypeIcons: Record<NoteType, React.ComponentType<{ className?: string }>> = {
    note: CloudUpload,
    quote: MessageSquare,
    emotion: Heart,
    behaviour: Users,
    pain: AlertTriangle,
    insight: Lightbulb,
    opportunity: Target
  };

  return (
    <Card className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-56 shadow-lg">
      <div className="p-4 space-y-4">

        {/* Note Types */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {mode === 'capturing' ? 'Capture Notes' : 'Analysis Notes'}
          </h3>
          <div className="space-y-2">
            {currentNoteTypes.map((noteType) => {
              const config = noteTypeConfig[noteType];
              const Icon = config.icon;
              
              return (
                <Button
                  key={noteType}
                  variant="outline"
                  size="sm"
                  className={`w-full justify-start text-left ${config.color} border-gray-200`}
                  onClick={() => onAddNote(noteType, reactFlowInstance)}
                  onDragStart={(e) => handleDragStart(e, noteType)}
                  draggable
                >
                  <Icon className="w-4 h-4 mr-2" />
                  <span className="text-sm">{config.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* AI Features Toggle */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700 mb-2">AI Features</h3>
          
          {isLiveGuidanceAvailable && (
            <ProFeatureWrapper feature="Live Guidance" description="Get real-time AI guidance during your interview">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <HelpCircle className="w-4 h-4" />
                  <span className="text-sm">Live Guidance</span>
                  {!isProUser && <Lock className="w-3 h-3 text-gray-400" />}
                </div>
                {onToggleLiveGuidance && isProUser && (
                  <Switch
                    checked={liveGuidanceEnabled}
                    onCheckedChange={onToggleLiveGuidance}
                  />
                )}
              </div>
            </ProFeatureWrapper>
          )}
        </div>

        {/* Organization Controls */}
        {(onGroupByType || onTimelineToggle) && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Organization</h3>
              
              {onGroupByType && (
                <div className="space-y-2">
                  <Button
                    onClick={isGrouped ? onUngroup : onGroupByType}
                    variant={isGrouped ? "default" : "outline"}
                    size="sm"
                    className="w-full justify-start"
                  >
                    {isGrouped ? (
                      <>
                        <Shuffle className="w-4 h-4 mr-2" />
                        Scatter Notes
                      </>
                    ) : (
                      <>
                        <Grid3x3 className="w-4 h-4 mr-2" />
                        Group by Type
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {onTimelineToggle && (
                <div className="space-y-2">
                  <Button
                    onClick={onTimelineToggle}
                    variant={isTimelineSorted ? "default" : "outline"}
                    size="sm"
                    className="w-full justify-start"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {isTimelineSorted ? 'Exit Timeline' : 'Timeline View'}
                  </Button>
                  
                  {isTimelineSorted && onSortChange && (
                    <div className="flex space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs justify-start"
                        onClick={() => onSortChange('newest')}
                      >
                        <ArrowDown className="w-3 h-3 mr-1" />
                        Newest
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs justify-start"
                        onClick={() => onSortChange('oldest')}
                      >
                        <ArrowUp className="w-3 h-3 mr-1" />
                        Oldest
                      </Button>
                    </div>
                   )}
                 </div>
               )}

               {/* AI Organization */}
               {onAiOrganize && (
                 <ProFeatureWrapper feature="AI Organization" description="Let AI intelligently organize your notes based on timeline and relevancy">
                   <Button
                     onClick={isProUser ? onAiOrganize : undefined}
                     variant="outline"
                     size="sm"
                     className="w-full justify-start"
                     disabled={isAiOrganizing}
                   >
                     <Sparkles className="w-4 h-4 mr-2" />
                     {isAiOrganizing ? 'Organizing...' : 'AI Organize'}
                     {!isProUser && <Lock className="w-3 h-3 ml-auto text-gray-400" />}
                   </Button>
                 </ProFeatureWrapper>
               )}

               {/* Note Counts Summary */}
              {totalNotes > 0 && (
                <div className="text-xs bg-gray-50 rounded p-2 space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Note Counts</div>
                  {Object.entries(noteTypeCounts).map(([type, count]) => {
                    if (count === 0) return null;
                    const Icon = noteTypeIcons[type as NoteType];
                    return (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Icon className="w-3 h-3 text-gray-500" />
                          <span className="text-gray-600">{noteTypeLabels[type as NoteType]}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs h-4">
                          {count}
                        </Badge>
                      </div>
                    );
                  })}
                  <div className="border-t pt-1 mt-2">
                    <div className="flex items-center justify-between font-medium">
                      <span>Total</span>
                      <Badge variant="default" className="h-4">{totalNotes}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* Action Buttons */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Actions</h3>
          
          {onOpenHighlights && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={onOpenHighlights}
            >
              <Star className="w-4 h-4 mr-2 text-yellow-500" />
              <span className="text-sm">View Highlights</span>
            </Button>
          )}

          <ProFeatureWrapper feature="AI Analysis" description="Analyze your interview notes with AI">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={isProUser ? onAnalyze : undefined}
              disabled={isAnalyzing}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              <span className="text-sm">{isAnalyzing ? 'Analyzing...' : 'Analyze Interview'}</span>
              {!isProUser && <Lock className="w-3 h-3 ml-auto text-gray-400" />}
            </Button>
          </ProFeatureWrapper>

          {/* Usage Indicator for Pro users */}
          {isProUser && (
            <div className="mt-2 p-2 bg-gray-50 rounded">
              <UsageIndicator currentUsage={currentUsage} limit={limit} className="text-xs" />
            </div>
          )}

          {isLiveGuidanceAvailable && (
            <ProFeatureWrapper feature="Live Guidance" description="Get real-time AI guidance during interviews">
              <Button
                variant="outline"  
                size="sm"
                className="w-full justify-start"
                onClick={isProUser ? onOpenGuidance : undefined}
                disabled={(!liveGuidanceEnabled && isProUser)}
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                <span className="text-sm">Live Guidance</span>
                {!isProUser && <Lock className="w-3 h-3 ml-auto text-gray-400" />}
              </Button>
            </ProFeatureWrapper>
          )}
        </div>

        {/* Help Text */}
        <div className="text-xs text-gray-500 mt-4 p-2 bg-gray-50 rounded">
          <p className="mb-1">💡 <strong>Tip:</strong> Drag notes directly onto the canvas or click to add them.</p>
          <p>⭐ Star important notes to highlight key insights across interviews.</p>
        </div>
      </div>
    </Card>
  );
};
